import { useState, useRef, useEffect } from "react";
import { X, MapPin, Trash2, Send, Loader2, Info } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";

/**
 * Customer-facing annotation modal: opens with a layout image, lets the user
 * drop numbered pins anywhere on the image and attach a comment to each pin.
 * On submit, calls POST /api/layout-requests/by-order/:orderId/comment with
 * { text, annotations: [{x,y,label,text}], image_url }.
 *
 * x/y are stored as percentages (0..100) of the image's natural display size
 * so the markup re-renders correctly across any future viewport size.
 */
const LayoutAnnotator = ({ open, onClose, file, orderId, onSaved }) => {
  const [pins, setPins] = useState([]);
  const [activePin, setActivePin] = useState(null);
  const [overall, setOverall] = useState("");
  const [busy, setBusy] = useState(false);
  const imgRef = useRef(null);
  const imgWrapRef = useRef(null);

  useEffect(() => {
    if (open) { setPins([]); setActivePin(null); setOverall(""); }
  }, [open, file?.url]);

  if (!open || !file) return null;

  const onImgClick = (e) => {
    // Ignore clicks that come from a pin or its popover
    if (e.target.closest("[data-annot-pin]")) return;
    const rect = imgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const label = pins.length + 1;
    const id = `p_${Date.now()}`;
    const next = [...pins, { id, x, y, label, text: "" }];
    setPins(next);
    setActivePin(id);
  };

  const updatePinText = (id, text) => {
    setPins((prev) => prev.map((p) => (p.id === id ? { ...p, text } : p)));
  };
  const removePin = (id) => {
    const remaining = pins.filter((p) => p.id !== id).map((p, i) => ({ ...p, label: i + 1 }));
    setPins(remaining);
    if (activePin === id) setActivePin(null);
  };

  const submit = async () => {
    const cleanPins = pins
      .map((p) => ({ x: p.x, y: p.y, label: p.label, text: (p.text || "").trim() }))
      .filter((p) => p.text || pins.length === 1); // allow single empty pin? Force text:
    const missing = cleanPins.filter((p) => !p.text);
    if (pins.length > 0 && missing.length > 0) {
      toast.error("Please add a note to each pin (or remove empty pins)");
      return;
    }
    if (pins.length === 0 && !overall.trim()) {
      toast.error("Drop at least one pin or type an overall comment");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/layout-requests/by-order/${orderId}/comment`, {
        text: overall.trim(),
        annotations: cleanPins,
        image_url: file.url,
      });
      toast.success("Feedback sent to the design team");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send feedback");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="layout-annotator-modal">
      <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[92vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#E2E8F0]">
          <div className="flex items-center gap-2 min-w-0">
            <MapPin className="h-5 w-5 text-[#06B6D4] shrink-0" />
            <div className="min-w-0">
              <h3 className="font-display text-base font-bold text-[#0B1E3F] truncate" data-testid="annotator-file-name">
                Mark up: {file.name}
              </h3>
              <p className="text-[11px] text-neutral-500 inline-flex items-center gap-1">
                <Info className="h-3 w-3" /> Click anywhere on the image to drop a pin, then add a note.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-neutral-100"
            data-testid="annotator-close-btn"><X className="h-5 w-5" /></button>
        </div>

        {/* Body */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 flex-1 overflow-hidden">
          {/* Image canvas */}
          <div className="lg:col-span-3 bg-[#0B1E3F] flex items-center justify-center overflow-auto p-4">
            <div ref={imgWrapRef}
              className="relative inline-block max-w-full max-h-[70vh]"
              style={{ cursor: "crosshair" }}
              onClick={onImgClick}
              data-testid="annotator-canvas">
              <img ref={imgRef} src={fileUrl(file.url)} alt={file.name}
                className="block max-w-full max-h-[70vh] object-contain select-none pointer-events-none"
                draggable={false} />
              {pins.map((p) => (
                <button key={p.id}
                  data-annot-pin
                  data-testid={`annotator-pin-${p.label}`}
                  onClick={(e) => { e.stopPropagation(); setActivePin(p.id); }}
                  style={{ left: `${p.x}%`, top: `${p.y}%` }}
                  className={`absolute -translate-x-1/2 -translate-y-1/2 h-7 w-7 rounded-full text-xs font-bold shadow-lg border-2 transition-transform ${activePin === p.id ? "bg-[#06B6D4] text-white border-white scale-110" : "bg-white text-[#0B1E3F] border-[#06B6D4] hover:scale-105"}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Side panel — pin list & overall comment */}
          <div className="lg:col-span-2 flex flex-col bg-white border-l border-[#E2E8F0] overflow-hidden">
            <div className="p-4 border-b border-[#E2E8F0]">
              <p className="text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-1">
                Pins ({pins.length})
              </p>
              <p className="text-[11px] text-neutral-400">Each pin is a precise revision request our designers will see exactly where you mean.</p>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="annotator-pin-list">
              {pins.length === 0 && (
                <div className="text-center py-10 text-neutral-400">
                  <MapPin className="h-10 w-10 mx-auto opacity-30" />
                  <p className="text-xs mt-2">No pins yet. Click on the image to start.</p>
                </div>
              )}
              {pins.map((p) => (
                <div key={p.id}
                  data-testid={`annotator-pin-row-${p.label}`}
                  className={`rounded-xl border p-3 transition-colors ${activePin === p.id ? "border-[#06B6D4] bg-[#06B6D4]/5" : "border-[#E2E8F0]"}`}
                  onClick={() => setActivePin(p.id)}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0B1E3F]">
                      <span className="h-5 w-5 rounded-full bg-[#06B6D4] text-white text-[10px] flex items-center justify-center">{p.label}</span>
                      Pin {p.label}
                    </span>
                    <button onClick={(e) => { e.stopPropagation(); removePin(p.id); }}
                      data-testid={`annotator-pin-remove-${p.label}`}
                      className="p-1 rounded hover:bg-red-50 text-red-500">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <textarea
                    data-testid={`annotator-pin-text-${p.label}`}
                    value={p.text}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => updatePinText(p.id, e.target.value)}
                    placeholder="e.g. 'make this logo bigger' or 'change to bold red'"
                    className="pav-input text-xs min-h-[60px] w-full" />
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-[#E2E8F0] space-y-2">
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-500">Overall comment (optional)</label>
              <textarea
                data-testid="annotator-overall-text"
                value={overall}
                onChange={(e) => setOverall(e.target.value)}
                placeholder="Anything not tied to a specific spot?"
                className="pav-input text-xs min-h-[50px] w-full" />
              <button onClick={submit} disabled={busy}
                data-testid="annotator-submit-btn"
                className="w-full btn-primary text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {busy ? "Sending…" : `Send ${pins.length} pin${pins.length === 1 ? "" : "s"} to design team`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LayoutAnnotator;
